import React, { useState } from "react";
import { closeMyOrganization } from "../../api/OrgAPI";
import { logApiError, getUserMessage } from "../../api/apiError";

interface Props {
    token: string;
    /** Called after a successful close — should log the user out. */
    onClosed?: () => void;
}

/** ORG_ROOT-only: cancels the organization's account. Password re-confirmed;
 *  the org is closed, member logins are blocked, listings are hidden. */
const CloseOrganizationPanel: React.FC<Props> = ({ token, onClosed }) => {
    const [open, setOpen] = useState(false);
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleClose = async () => {
        const sure = window.confirm(
            "Close your organization's account?\n\n" +
            "All members of the organization will be signed out and unable to log in, " +
            "and all your listings will be hidden from search. Nothing is deleted — " +
            "contact support if you later want the account reopened."
        );
        if (!sure) return;
        setBusy(true);
        setError(null);
        try {
            await closeMyOrganization(token, password);
            window.alert("Your organization's account has been closed.");
            onClosed?.();
        } catch (e) {
            logApiError(e);
            setError(getUserMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="section-box" style={{ borderColor: "var(--danger, #b91c1c)" }}>
            <h4 style={{ marginBottom: "0.5rem" }}>Close Organization Account</h4>
            <div className="item-description" style={{ marginBottom: "0.75rem" }}>
                Cancels your organization's account: everyone in the organization is
                blocked from logging in and all listings are hidden from search.
                Nothing is deleted, so support can reopen the account later.
            </div>

            {!open ? (
                <button className="btn-secondary" onClick={() => setOpen(true)}>
                    Close organization account…
                </button>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxWidth: "360px" }}>
                    <label>
                        <div>Confirm your password</div>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={busy}
                            autoComplete="current-password"
                        />
                    </label>
                    <div className="button-group">
                        <button
                            className="danger"
                            onClick={handleClose}
                            disabled={busy || !password}
                        >
                            {busy ? "Closing…" : "Close the account"}
                        </button>
                        <button
                            className="btn-secondary"
                            onClick={() => { setOpen(false); setPassword(""); setError(null); }}
                            disabled={busy}
                        >
                            Cancel
                        </button>
                    </div>
                    {error && <div className="error-message">{error}</div>}
                </div>
            )}
        </div>
    );
};

export default CloseOrganizationPanel;